import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import {
    ArrowSyncFilled,
    ArrowSyncRegular,
    ArrowTrendingLinesFilled,
    ArrowTrendingLinesRegular,
    bundleIcon,
    CalendarAddFilled,
    CalendarAddRegular,
    CalendarDayRegular,
    ClipboardTaskFilled,
    ClipboardTaskRegular,
    DocumentOnePageRegular,
    EditFilled,
    EditRegular,
    FlowchartRegular,
    InfoFilled,
    InfoRegular,
    MailAddFilled,
    MailAddRegular,
    MoneyRegular,
    PrintFilled,
    PrintRegular,
    TimerFilled,
    TimerRegular,
} from '@fluentui/react-icons'
import {
    Button,
    Card,
    makeStyles,
    mergeClasses,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    shorthands,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import { DocumentGenerationParams, DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { ViewFileProps } from '../../../pages/File/ViewFile'
import useAxios from '../../../hooks/useAxios'
import { useDispatch } from 'react-redux'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../../hooks/useRoles'
import useModules from '../../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useSharePoint from '../../../hooks/useSharePoint'
import { DRIVE_ITEM_CONFIG, getFullPath } from '../../../components/TextField/Folder/FolderField'
import { IGraphResponse } from '../../../context/AxiosContext'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import PersonAvatar from '../../../components/PersonAvatar'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import { toast } from 'react-toastify'
import { SharePointIcon } from '../../../components/Icon/SharePointIcon'
import OverflowToolbarPanel, { HammockToolbarButtonData } from '../../../components/Overflow/OverflowToolbarPanel'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import { FileRateType, ModuleType } from '../../../context/GlobalContext'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../hooks/file/useFilePages'
import useCurrency from '../../../hooks/useCurrency'
import useWorkflows from '../../../hooks/useWorkflows'
import { FileAction } from '../../../config/fileConfig'
import DialogButton from '../../components/Button/DialogButton'
import ComposeEmailDialog from '../../../components/Dialog/OutlookItem/ComposeEmailDialog'
import ComposeAppointmentDialog from '../../../components/Dialog/OutlookItem/ComposeAppointmentDialog'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import IconValue from '../../../components/IconValue/IconValueV9'
import MoneyText from '../../../components/Text/Currency/MoneyText'
import { formatFullDate } from '../../../helpers/dateHelper'
import { toastContainerId } from '../../../pages/App'

const ArrowSyncIcon = bundleIcon(ArrowSyncFilled, ArrowSyncRegular)
const ArrowTrendingLinesIcon = bundleIcon(ArrowTrendingLinesFilled, ArrowTrendingLinesRegular)
const CalendarAddIcon = bundleIcon(CalendarAddFilled, CalendarAddRegular)
const ClipboardTaskIcon = bundleIcon(ClipboardTaskFilled, ClipboardTaskRegular)
const EditIcon = bundleIcon(EditFilled, EditRegular)
const InfoIcon = bundleIcon(InfoFilled, InfoRegular)
const MailAddIcon = bundleIcon(MailAddFilled, MailAddRegular)
const PrintIcon = bundleIcon(PrintFilled, PrintRegular)
const TimerIcon = bundleIcon(TimerFilled, TimerRegular)

const useStyles = makeStyles({
    card: {
        ...shorthands.margin(tokens.spacingHorizontalS, 0),
        marginTop: 0,
        padding: tokens.spacingHorizontalS,
    },
    line: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        width: '100%',
        minHeight: '32px',
    },
    lineSpaceBetween: {
        justifyContent: 'space-between',
    },
    lineSpaceBetweenExpand: {
        display: 'flex',
        width: '32px',
    },
    lineWrap: {
        flexWrap: 'wrap',
        alignItems: 'flex-start',
    },
    lineToolbar: {
        marginLeft: `calc( 0px - (( var( --spacingHorizontalS ) ) / 2 ))`,
    },
    toolbar: {
        minWidth: 0,
        flexGrow: 1,
        padding: 0,
    },
    name: {
        maxHeight: '2.5rem',
        overflow: 'hidden',
    },
})

type FileCardParamsDto = DocumentGenerationParams<{
    fileId: string
}>

export default function ViewFileHeader({ file, fileId }: ViewFileProps): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const { sharePoint } = useSharePoint()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { getWorkflowById } = useWorkflows()

    const viewportNarrow = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    const submitRequest = useSubmitDocumentGenerationRequest<FileCardParamsDto>(DocumentGenerationType.FileCard)

    const { currency, currencyName } = useCurrency(file.current.data.currency)
    const routeMatch = useRouteMatch<{ fileId: string }>()

    const [currentDocumentRootUrl, setCurrentDocumentRootUrl] = useState<string>()
    useEffect(() => {
        if (sharePoint?.list?.webUrl && file.current.data.documentRootId) {
            axios.get(`/items/${file.current.data.documentRootId}`, DRIVE_ITEM_CONFIG).then((response: IGraphResponse<DriveItem>) => {
                if (response.data) {
                    setCurrentDocumentRootUrl(sharePoint?.list?.webUrl + '/' + encodeURIComponent(getFullPath(response.data)))
                }
            })
        }
    }, [sharePoint])

    const copyToSubjectClick = (value: string) => {
        if (isAppContext([AppContext.Compose]) && Office.context.mailbox.item?.subject?.getAsync) {
            Office.context.mailbox.item?.subject?.getAsync(result => {
                if (result.value.toLowerCase().indexOf(value.toLowerCase()) === -1) {
                    Office.context.mailbox.item?.subject.setAsync(`${result.value} ${value}`)
                }
            })
        } else {
            navigator.clipboard.writeText(`${value}`)
        }
        toast(translate('action.copy.copied'), { autoClose: 1000, containerId: toastContainerId })
    }

    const copyToBodyClick = (value: string) => {
        if (isAppContext([AppContext.Compose]) && Office.context.mailbox.item?.body?.prependAsync) {
            Office.context.mailbox.item?.body.prependAsync(value)
        } else {
            navigator.clipboard.writeText(`${value}`)
            toast(translate('action.copy.copied'), { autoClose: 1000, containerId: toastContainerId })
        }
    }

    const buttons = useMemo<HammockToolbarButtonData[]>(
        (): HammockToolbarButtonData[] =>
            [
                {
                    icon: <TimerIcon />,
                    titleKey: 'action.create.time.title',
                    filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                    onClick: () => history.push(generatePath(`${fileRoutePrefix}/time/add`, routeMatch.params)),
                },
                {
                    icon: <ClipboardTaskIcon />,
                    titleKey: 'action.create.task.title',
                    filter: () => modules.any([ModuleType.Tasks, ModuleType.Workflow]) && roles.any(rolesTasks),
                    onClick: () =>
                        history.push(
                            generatePath(`${fileRoutePrefix}/${modules.has(ModuleType.Workflow) ? 'workflow' : 'task'}/add`, routeMatch.params),
                        ),
                },
                {
                    icon: <MailAddIcon />,
                    titleKey: 'action.create.email',
                    filter: () => !isAppContext(AppContext.Compose),
                    render: () => (
                        <DialogButton
                            tooltipProps={{ content: translate('action.create.email') }}
                            buttonProps={{ appearance: 'subtle', icon: <MailAddIcon /> }}
                            dialog={ComposeEmailDialog}
                            dialogProps={{ file: file.current.data, parties: file.party.data }}
                        />
                    ),
                },
                {
                    icon: <CalendarAddIcon />,
                    titleKey: 'action.create.appointment',
                    filter: () => isOfficeInitialized() && !isAppContext(AppContext.Compose),
                    render: () => (
                        <DialogButton
                            tooltipProps={{ content: translate('action.create.appointment') }}
                            buttonProps={{ appearance: 'subtle', icon: <CalendarAddIcon /> }}
                            dialog={ComposeAppointmentDialog}
                            dialogProps={{ file: file.current.data, parties: file.party.data }}
                        />
                    ),
                },
                {
                    icon: <ArrowTrendingLinesIcon />,
                    titleKey: 'action.create.invoice.title',
                    filter: () => modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]),
                    onClick: () => history.push(`/invoice/add`, { fileIds: [fileId] }),
                },
                {
                    icon: <PrintIcon />,
                    titleKey: 'file.pdf',
                    filter: () => !!file.current.data.documentRootId,
                    onClick: () => submitRequest({ fileId }),
                },
                {
                    icon: <ArrowSyncIcon />,
                    titleKey: 'file.refresh',
                    filter: () => roles.none([Role.FileLimited]),
                    onClick: () => dispatch({ type: FileAction.ResetCurrent }),
                },
                {
                    icon: <SharePointIcon />,
                    titleKey: 'menu.sharepoint',
                    filter: () => !!file.current.data.documentRootId,
                    onClick: () => window.open(currentDocumentRootUrl, '_blank'),
                },
            ].filter((item: HammockToolbarButtonData) => (item.filter ? item.filter() : true)),
        [roles.loaded, modules.loaded, currentDocumentRootUrl],
    )

    return (
        <Card className={styles.card}>
            <div className={mergeClasses(styles.line, styles.lineSpaceBetween)}>
                <div className={styles.line}>
                    <PersonAvatar name={file.current.data.owner?.fullName} color="colorful" size={24} />
                    <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                        <Text weight={'semibold'} onClick={() => copyToSubjectClick(`[${file.current.data.reference}]`)}>
                            {file.current.data.reference}
                        </Text>
                    </Tooltip>
                </div>
                <div>
                    <Popover withArrow positioning={'below-end'}>
                        <PopoverTrigger disableButtonEnhancement>
                            <Button icon={<InfoIcon />} appearance={'transparent'} />
                        </PopoverTrigger>
                        <PopoverSurface tabIndex={-1}>
                            <div>
                                {file.current.data.description && (
                                    <div className={mergeClasses(styles.line, styles.lineWrap)}>
                                        <IconValue icon={<DocumentOnePageRegular />}>
                                            <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                                                <Text onClick={() => copyToBodyClick(file.current.data.description)}>
                                                    {file.current.data.description}
                                                </Text>
                                            </Tooltip>
                                        </IconValue>
                                    </div>
                                )}
                                <div className={styles.line}>
                                    <IconValue icon={<MoneyRegular />}>
                                        {file.current.data.fileRateType === FileRateType.File ? (
                                            <MoneyText amount={file.current.data.rate} currency={currency} />
                                        ) : file.current.data.fileRateType === FileRateType.UserTier ? (
                                            `${translate('file.rate.usertier.label')}, ${currencyName} (${currency.symbol})`
                                        ) : (
                                            `${translate('file.rate.partytier.label')}, ${currencyName} (${currency.symbol})`
                                        )}
                                    </IconValue>
                                </div>
                                {modules.has(ModuleType.Workflow) && file.current.data.workflows?.length > 0 && (
                                    <div className={styles.line}>
                                        <IconValue icon={<FlowchartRegular />}>
                                            <Text>
                                                {(file.current.data.workflows || [])
                                                    .map(workflow => getWorkflowById(workflow)?.name)
                                                    .sort()
                                                    .join(', ')}
                                            </Text>
                                        </IconValue>
                                    </div>
                                )}
                                {file.current.data.dateOfIncident && (
                                    <div className={styles.line}>
                                        <IconValue icon={<CalendarDayRegular />}>
                                            <Text>{formatFullDate(file.current.data.dateOfIncident)}</Text>
                                        </IconValue>
                                    </div>
                                )}
                            </div>
                        </PopoverSurface>
                    </Popover>
                </div>
            </div>
            <div className={mergeClasses(styles.line, styles.lineWrap)}>
                <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                    <Text className={viewportNarrow && styles.name} onClick={() => copyToSubjectClick(file.current.data.name)}>
                        {file.current.data.name}
                    </Text>
                </Tooltip>
            </div>
            <div className={mergeClasses(styles.line, styles.lineToolbar, styles.lineSpaceBetween)}>
                <OverflowToolbarPanel
                    toolbarProps={{ size: 'small', className: styles.toolbar }}
                    toolbarButtonProps={{ appearance: 'subtle' }}
                    containerClassName={mergeClasses(styles.line, styles.toolbar)}
                    buttons={buttons}
                    overflowButtonLabelKey={'base.item.label'}
                />
                <div className={mergeClasses(styles.line, styles.lineSpaceBetweenExpand)}>
                    <Tooltip content={translate('file.edit.action')} relationship={'description'} withArrow>
                        <Button icon={<EditIcon />} appearance={'subtle'} onClick={() => history.push(`/file/${fileId}/edit`)} />
                    </Tooltip>
                </div>
            </div>
        </Card>
    )
}
